import type { FC } from "react";
import React from "react";

import type {
  Action,
  ActionAction,
  Pages,
  WelcomeOfferBannerType,
} from "~/core";
import { WelcomeOfferCards } from "~/features/welcome-offer-banner/components/WelcomeOfferCards";
import { Background, Image } from "~/shared/ui-kit";
import { Flex } from "~/shared/ui-kit/flex/Flex";
import { Title } from "~/shared/ui-kit/title/Title";
import type { ImageParams } from "~/shared/utils/directus/useDirectus";

type WelcomeOfferBannerBannerProps = {
  data: WelcomeOfferBannerType;
  getUrl: (page: Pages) => string;
  onCallAction: (action: ActionAction) => void;
  getImageUrl: (imageId: string, params?: ImageParams) => string;
  isActionInactive: (action: Action) => boolean;
};

export const WelcomeOfferBanner: FC<WelcomeOfferBannerBannerProps> = (
  props,
) => {
  const { data, getImageUrl, isActionInactive } = props;

  const middleIndex = (data?.items && Math.ceil(data.items.length / 2)) ?? 0;
  const firstColumnCards = data?.items?.slice(0, middleIndex);
  const secondColumnCards = data?.items?.slice(middleIndex);

  const isFullWidthCard = (id: ID): boolean => {
    const activeItems = data.items?.filter((card) => {
      if (card?.welcome_offer_banner_items_id?.action) {
        return !isActionInactive(card.welcome_offer_banner_items_id.action);
      }

      return true;
    });

    return !!(
      activeItems &&
      activeItems.length % 2 !== 0 &&
      activeItems?.[activeItems.length - 1]?.id === id
    );
  };

  return (
    <Background
      bgValue={
        data.backgroundImage
          ? getImageUrl(data.backgroundImage.id, {
              format: "webp",
              width: 800,
            })
          : (data.backgroundColor ?? "")
      }
      mdBgValue={
        data.backgroundImage
          ? getImageUrl(data.backgroundImage.id, {
              format: "webp",
              width: 1920,
            })
          : (data.backgroundColor ?? "")
      }
      className="pt-10 md:pt-[86px]"
    >
      <Title
        as="h2"
        size="large"
        weight="bold"
        className="bg-sunrise uppercase bg-clip-text text-transparent leading-8 text-center mb-10 md:mb-[66px] px-6"
      >
        {data?.title}
      </Title>

      <Flex
        className="flex-wrap lg:flex-nowrap"
        md={{ justify: "center", gap: 20 }}
        align="center"
        justify="space-evenly"
        gap={16}
      >
        <WelcomeOfferCards
          {...props}
          isFullWidthCard={isFullWidthCard}
          data={firstColumnCards}
        />

        <Image
          className="order-3 flex-grow md:flex-grow-0 flex justify-center lg:order-none md:max-w-[610px]"
          value={getImageUrl(data.image?.id ?? "", {
            format: "webp",
            width: 360,
          })}
          mdValue={getImageUrl(data.image?.id ?? "", {
            format: "webp",
            width: 610,
          })}
          dValue={getImageUrl(data.image?.id ?? "", {
            format: "webp",
            width: 610,
          })}
        />

        <WelcomeOfferCards
          {...props}
          isFullWidthCard={isFullWidthCard}
          data={secondColumnCards}
          className="w-full md:w-auto mb-[67px] md:mb-0"
        />
      </Flex>
    </Background>
  );
};
